import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { SearchableSelect } from "@/components/ui/SearchableSelect";

/**
 * Hook + Pickers — the single source of truth for Subject + Grade dropdowns
 * across the entire app. Pulls from /api/subjects and /api/grades on mount
 * and exposes them as searchable selects. Replaces every free-text subject/grade
 * input previously scattered across AI tool pages.
 */
export function useCurriculumOptions() {
  const [subjects, setSubjects] = useState([]);
  const [grades, setGrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [s, g] = await Promise.all([api.get("/subjects"), api.get("/grades")]);
      setSubjects(s.data.subjects || []);
      setGrades(g.data.grades || []);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);
  return { subjects, grades, loading, reload };
}

/**
 * SubjectPicker — searchable subject dropdown.
 *
 * Props:
 *  - value: subject_id (string)
 *  - onChange: (subject_id, subject_name, grade_id, grade_name) => void
 *  - filterGradeId: optional grade_id to filter subjects to that grade
 *  - dark, testid, className, placeholder
 */
export function SubjectPicker({ value, onChange, filterGradeId = "", placeholder = "Pick a subject…", dark = false, testid = "subject-picker", className = "" }) {
  const { subjects, grades, loading, reload } = useCurriculumOptions();
  const [quickAdd, setQuickAdd] = useState(null); // 'subject' | 'grade'
  const [qName, setQName] = useState("");
  const [qGrade, setQGrade] = useState("");
  const [qBusy, setQBusy] = useState(false);
  const filtered = filterGradeId ? subjects.filter((s) => s.grade_id === filterGradeId) : subjects;
  const options = filtered.map((s) => ({
    value: s.id,
    label: s.name,
    hint: s.grade_name || undefined,
    icon: <span className="w-2 h-2 rounded-full inline-block" style={{ background: s.color || "#FFB400" }} />,
  }));
  const handle = (sid) => {
    const s = subjects.find((x) => x.id === sid);
    onChange?.(sid, s?.name || "", s?.grade_id || "", s?.grade_name || "");
  };

  const quickCreate = async (close) => {
    const name = qName.trim();
    if (!name) return;
    setQBusy(true);
    try {
      if (quickAdd === "grade") {
        await api.post("/grades", { name });
        window?.dispatchEvent?.(new Event("curriculum-updated"));
      } else {
        const { data } = await api.post("/subjects", { name, grade_id: qGrade || "" });
        const s = data.subject;
        onChange?.(s.id, s.name, s.grade_id || "", s.grade_name || "");
        close?.();
      }
      await reload();
      setQuickAdd(null); setQName(""); setQGrade("");
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Could not create");
    } finally { setQBusy(false); }
  };

  // Quick-add footer inside the dropdown — create a subject or a class/grade
  // without leaving the current form.
  const footer = (close) => quickAdd ? (
    <div className="p-2 space-y-1.5" onKeyDown={(e) => e.stopPropagation()}>
      <input
        autoFocus value={qName} onChange={(e) => setQName(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); quickCreate(close); } }}
        placeholder={quickAdd === "grade" ? "Class/Grade name (eg: Grade 7)" : "Subject name (eg: Physics)"}
        data-testid="quick-add-name"
        className="w-full text-xs border border-neutral-300 rounded-md px-2.5 py-2 outline-none focus:border-[#0A0C10]"
      />
      {quickAdd === "subject" && grades.length > 0 && (
        <select value={qGrade} onChange={(e) => setQGrade(e.target.value)} data-testid="quick-add-grade"
          className="w-full text-xs border border-neutral-300 rounded-md px-2 py-2 outline-none bg-white">
          <option value="">No grade (optional)</option>
          {grades.map((g) => <option key={g.id} value={g.id}>{g.name}</option>)}
        </select>
      )}
      <div className="flex gap-1.5">
        <button type="button" onClick={() => quickCreate(close)} disabled={qBusy || !qName.trim()} data-testid="quick-add-save"
          className="flex-1 text-xs font-bold bg-[#0A0C10] text-white rounded-md py-1.5 disabled:opacity-50">
          {qBusy ? "Adding…" : `Add ${quickAdd}`}
        </button>
        <button type="button" onClick={() => { setQuickAdd(null); setQName(""); }} className="text-xs px-2 text-neutral-500 hover:text-black">Cancel</button>
      </div>
    </div>
  ) : (
    <div className="flex gap-1">
      <button type="button" onClick={() => setQuickAdd("subject")} data-testid="quick-add-subject-btn"
        className="flex-1 text-xs font-semibold px-2 py-1.5 rounded-md hover:bg-neutral-100 text-left">＋ Add subject</button>
      <button type="button" onClick={() => setQuickAdd("grade")} data-testid="quick-add-class-btn"
        className="flex-1 text-xs font-semibold px-2 py-1.5 rounded-md hover:bg-neutral-100 text-left">＋ Add class/grade</button>
    </div>
  );

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={handle}
      placeholder={loading ? "Loading subjects…" : (subjects.length === 0 ? "No subjects yet — add one below" : placeholder)}
      searchPlaceholder="Search subjects…"
      dark={dark}
      testid={testid}
      className={className}
      disabled={false}
      footer={footer}
    />
  );
}

/**
 * GradePicker — searchable grade dropdown.
 *
 * Props:
 *  - value: grade_id
 *  - onChange: (grade_id, grade_name) => void
 *  - includeNone: include a "Not linked" option
 */
export function GradePicker({ value, onChange, includeNone = false, placeholder = "Pick a grade…", dark = false, testid = "grade-picker", className = "" }) {
  const { grades, loading } = useCurriculumOptions();
  const options = grades.map((g) => ({ value: g.id, label: g.name, hint: g.description || undefined }));
  if (includeNone) options.unshift({ value: "", label: "— Not linked —" });
  const handle = (gid) => {
    const g = grades.find((x) => x.id === gid);
    onChange?.(gid, g?.name || "");
  };
  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={handle}
      placeholder={loading ? "Loading grades…" : (grades.length === 0 ? "No grades yet — add one under Grades in the sidebar" : placeholder)}
      searchPlaceholder="Search grades…"
      dark={dark}
      testid={testid}
      className={className}
      disabled={!loading && grades.length === 0 && !includeNone}
    />
  );
}
